import { useContext, useEffect, useState } from "react";
import SlotDetailCellRenderer from "./SlotDetailCellRenderer";
import {OwcChip, OwcGrid, OwcTooltip, OwcTypography} from "@one/react";
import {
  valueOrEmpty,
  formatUserDisplay,
  USER_FORMAT_PRESETS,
} from "../../utils/helpers/text";
import DLabGrid from "../DLabGrid/DLabGrid";
import { changeActionTypeToDisplay, isActionType } from "./helpFunctions";
import InlineEditableField from "./InlineEditableField/InlineEditableField";
import { InlineRichDescriptionField } from "./RichDescription";
import { APPROVE_BOOKING, QUICK_EDIT_BOOKING } from "../../gql/bookingapi/mutations";
import { useSelector } from "react-redux";
import { quickEditTracker } from "../../features/booking-operations/quickEditTracker";
import { withApollo, compose } from "react-apollo";
import { BookingMeta } from "../../constants";
import { ElnLinkRenderer } from "@digitallab/grid-common-frontend-components";
import HazardousApprovalActions from "../../features/hazardous-materials/HazardousApprovalActions";
import AssistanceDetails from "./CommonPopOverModal/BookingEventDetails/AssistanceDetails";
import DateTimeRange from "./CommonPopOverModal/BookingEventDetails/DateTimeRange";
import CreatedByCreatedOn from "./CommonPopOverModal/BookingEventDetails/CreatedByCreatedOn";
import { projectDisplayText } from "../../features/user/redux/selectors";
import { PaginationContext } from "./pagination/PaginationContext";
import ChecklistRender from "../../features/equipment-list/ChecklistRender";
import ChangeBookingEndTimeModal from "../../features/my-booking/ChangeBookingEndTime/ChangeBookingEndTimeModal"; // ADD THIS IMPORT
import useChangeEndTimeModal from "../../features/my-booking/ChangeBookingEndTime/hooks/useChangeEndTimeModal";

const extractCommandIdFromMessage = (message) => {
  const match = String(message || "").match(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/i);
  return match?.[0] || null;
};

const resolveAssistantDisplayName = (assistantId, usersList) => {
  if (!assistantId) return null;
  const assistantUser = usersList?.find((user) => user.userId === assistantId);
  if (!assistantUser) return assistantId;
  return formatUserDisplay(assistantUser, USER_FORMAT_PRESETS.nameOnly);
};

const buildApproveBookingVariables = ({ bookingId, nextStatus, assistantId, assistantDuration, user }) => ({
  bookingId,
  approval: {
    status: nextStatus,
    assistant: assistantId ? [assistantId] : [],
    assistantTime: assistantId ? assistantDuration : null,
    approvedBy: user?.user,
    approvedByEmail: user?.email,
  },
  createdBy: user?.user,
  createdByEmail: user?.email,
});

const runAssistanceUpdateComplete = async (onAssistanceUpdateComplete) => {
  if (onAssistanceUpdateComplete) {
    await onAssistanceUpdateComplete();
  }
};

const buildInitialAssistanceState = (data, usersList) => {
  const assistantId = data?.assistent?.[0] || "";
  return {
    assistantId,
    assistantDuration: assistantId ? (data?.assistentTime ?? null) : null,
    assistantName: resolveAssistantDisplayName(assistantId, usersList),
  };
};

const checkIsBookingAdmin = (user) =>
  user?.roles?.some((role) => role === "BookingAdmin") ?? false;

const buildEffectiveBooking = (data, assistanceInlineState) => ({
  ...data,
  assistent: assistanceInlineState.assistantId ? [assistanceInlineState.assistantId] : [],
  assistentTime: assistanceInlineState.assistantId ? assistanceInlineState.assistantDuration : null,
});

const setFetchingState = (dispatchAction, setStatus, isFetching) => {
  dispatchAction({ type: "fetching", payload: isFetching });
  if (setStatus) setStatus(isFetching);
};

const throwQuickEditFailure = ({ payload, dispatchAction, setStatus, fallbackMessage }) => {
  setFetchingState(dispatchAction, setStatus, false);
  throw new Error(payload?.message || fallbackMessage);
};

const finalizeQuickEdit = async ({ payload, dispatchAction, setStatus, onSuccess }) => {
  if (payload?.commandId) {
    quickEditTracker.register(payload.commandId, "UPDATE", {
      onSuccess: async () => {
        setFetchingState(dispatchAction, setStatus, false);
        if (onSuccess) await onSuccess();
      },
      onError: () => {
        setFetchingState(dispatchAction, setStatus, false);
      },
    });
    return;
  }

  setFetchingState(dispatchAction, setStatus, false);
  if (onSuccess) await onSuccess();
};

const resolveBookingId = (data) => data?.bookingId ?? data?.id;

const canEditBookingDescription = ({ allowDescriptionEdit, isAdminContext, isBookingAdmin }) =>
  allowDescriptionEdit && (isAdminContext || isBookingAdmin);

const canEditHazardousMaterials = ({ canEditDescription, hazardousDeclaration }) =>
  canEditDescription && Boolean(hazardousDeclaration?.hasHazardousMaterials);

const canEditBookingAssistance = ({ allowAssistanceEdit, isAdminContext, isBookingAdmin }) =>
  allowAssistanceEdit && isAdminContext && isBookingAdmin;

const syncAssistanceInlineStateName = (prev, usersList) => {
  if (!prev.assistantId) return prev;

  const resolvedAssistantName = resolveAssistantDisplayName(prev.assistantId, usersList);
  if (resolvedAssistantName === prev.assistantName) return prev;

  return {
    ...prev,
    assistantName: resolvedAssistantName,
  };
};

const openAssistanceDialog = ({ data, setBookingForAssistance, setOpenAssistanceDialog, setIsEditMode }) => {
  if (!(setBookingForAssistance && setOpenAssistanceDialog && setIsEditMode)) return;

  setBookingForAssistance(data);
  setIsEditMode(true);
  setOpenAssistanceDialog(true);
};

const ProjectField = ({ data, projectLabel }) => {
  const projectAnchorId = `project_${data?.id ?? "row"}`;
  const hasProjectLabel = Boolean(projectLabel);
  const label = data?.redPandaProjects?.length ? "Projects" : "Project";
  const projectLabelStyle = {
    maxWidth: "340px",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  return (
    <div>
      <OwcTypography variant="caption">{label}</OwcTypography>
      <OwcTypography
        variant="subtitle2"
        style={projectLabelStyle}
        title={projectLabel || ""}
        id={projectAnchorId}
      >
        {projectLabel}
      </OwcTypography>
      {hasProjectLabel && (
        <OwcTooltip anchor={projectAnchorId}>
          <span style={{ whiteSpace: "pre-line" }}>
            {projectLabel.replaceAll(", ", "\n")}
          </span>
        </OwcTooltip>
      )}
    </div>
  );
};

const ActionTypeField = ({ type, elnId }) => {
  if (isActionType(type)) {
    return (
      <div>
        <OwcTypography variant="caption">Action type</OwcTypography>
        <OwcTypography variant="subtitle2">
          {valueOrEmpty(changeActionTypeToDisplay(type), false, "-")}
        </OwcTypography>
      </div>
    );
  }

  return (
    <div>
      <OwcTypography variant="caption">eLN ID</OwcTypography>
      <OwcTypography variant="subtitle2">
        <ElnLinkRenderer elnExperiment={elnId} />
      </OwcTypography>
    </div>
  );
};

const RejectionReasonField = ({ approval }) => {
  if (approval?.status !== "REJECTED") return null;

  return (
    <div>
      <OwcTypography variant="caption">
        Reason for rejection
      </OwcTypography>
      <OwcTypography variant="subtitle2">
        {valueOrEmpty(approval?.rejectionReason, false, "-")}
      </OwcTypography>
    </div>
  );
};

const getBookedUserKey = (bookedUser, index) => {
  if (bookedUser?.userId) return bookedUser.userId;
  if (bookedUser?.email) return bookedUser.email;
  if (bookedUser?.name) return `${bookedUser.name}-${index}`;
  return `unknown-${index}`;
};

const getBookedUserLabel = (bookedUser) => {
  if (!bookedUser) return "-";

  const givenName = bookedUser?.givenName ?? "";
  const familyName = bookedUser?.familyName ?? "";
  const identifier = bookedUser?.name || bookedUser?.email || "-";

  return `${givenName}  ${familyName}  (${identifier})`;
};

const BookingUsersField = ({ bookedUsers }) => (
  <div>
    <OwcTypography variant="caption">Booking for</OwcTypography>
    <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
      {bookedUsers?.map((bookedUser, index) => (
        <OwcChip
          key={getBookedUserKey(bookedUser, index)}
          icon={"user"}
          iconType="outlined"
          outlined
          round
        >
          {getBookedUserLabel(bookedUser)}
        </OwcChip>
      ))}
    </div>
  </div>
);

const handleHazardousMaterialsSave = async ({
  nextVal,
  bookingId,
  hazardousDeclaration,
  userEmail,
  client,
  setStatus,
  dispatchAction,
  onHazardousMaterialsUpdate,
}) => {
  if (!bookingId) return;
  const trimmed = (nextVal || "").trim();
  const current = (hazardousDeclaration?.hazardousMaterialsList || "").trim();
  if (trimmed === current) return;

  setFetchingState(dispatchAction, setStatus, true);

  const result = await client.mutate({
    mutation: QUICK_EDIT_BOOKING,
    fetchPolicy: "no-cache",
    variables: {
      input: {
        bookingId,
        hazardousDeclaration: {
          hasHazardousMaterials: !!hazardousDeclaration?.hasHazardousMaterials,
          hazardousMaterialsList: trimmed,
        },
        createdByEmail: userEmail,
      },
    },
  });
  const payload = result?.data?.quickEditBooking;
  if (!payload?.success) {
    throwQuickEditFailure({
      payload,
      dispatchAction,
      setStatus,
      fallbackMessage: "Hazardous materials update failed",
    });
  }

  await finalizeQuickEdit({
    payload,
    dispatchAction,
    setStatus,
    onSuccess: async () => {
      if (onHazardousMaterialsUpdate) await onHazardousMaterialsUpdate(trimmed);
    },
  });
};

const handleAssistanceSaved = ({ assistantId, assistantName, assistantDuration, _dataId, usersList, setAssistanceInlineState }) => {
  const resolvedAssistantId = assistantId || "";
  const nextInlineState = {
    assistantId: resolvedAssistantId,
    assistantDuration: assistantDuration ?? null,
    assistantName: assistantName || resolveAssistantDisplayName(resolvedAssistantId, usersList),
  };
  setAssistanceInlineState({ ...nextInlineState });
};

const processApproveBookingSuccess = async ({ payload, onAssistanceUpdateComplete }) => {
  if (!payload?.success) {
    return false;
  }

  const commandId = payload?.commandId || extractCommandIdFromMessage(payload?.message);
  if (commandId) {
    quickEditTracker.register(commandId, "UPDATE", {
      onSuccess: async () => {
        await runAssistanceUpdateComplete(onAssistanceUpdateComplete);
      },
    });
    return true;
  }

  await runAssistanceUpdateComplete(onAssistanceUpdateComplete);
  return true;
};

const ALL_COLUMNS = {
  Model: {
    field: "Model",
    headerName: "Model",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.equipmentModel, false, "-"),
  },
  serialNumber: {
    field: "SerialNumber",
    headerName: "Serial number",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.serialNumber, false, "-"),
  },
  equipmentId: {
    field: "Equipment ID",
    headerName: "Equipment ID",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.equipmentId, false, "-"),
  },
  equipmentNickName: {
    field: "Nickname",
    headerName: "Nickname",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.equipmentNickName, false, "-"),
  },
  location: {
    field: "Location",
    headerName: "Location",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.location, false, "-"),
  },
  softwareVersion: {
    field: "Software version",
    headerName: "Software version",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.softwareVersion, false, "-"),
  },
  belongingToGroup: {
    field: "Group",
    headerName: "Group",
    valueFormatter: (row) =>
      valueOrEmpty(row?.data?.equipment?.belongingToGroup, false, "-"),
  },
  checklists: {
    field: "checklists",
    headerName: "Documentation",
    cellRenderer: ChecklistRender,
    width: 60,
    suppressSizeToFit: true,
  },
};

const buildColumnDefs = (_preset) => {
  const base = [
    {
      field: "",
      cellRenderer: "agGroupCellRenderer",
      width: 80,
      pinned: "left",
    },
  ];
  return [...base, ...Object.keys(ALL_COLUMNS).map((c) => ALL_COLUMNS[c])];
};

const BookingDetailRenderer = ({
  data,
  client,
  isAdminContext = false,
  displayPreset,
  setOpenHazardousRejectDialog,
  setHazardousBookingToReject,
  setOpenAssistanceDialog,
  setBookingForAssistance,
  setIsEditMode,
  allowAssistanceEdit = false,
  allowDescriptionEdit = true,
  onDescriptionUpdate,
  onHazardousMaterialsUpdate,
  onAssistanceUpdateComplete,
  status: _status,
  setStatus,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [rowData] = useState(data?.bookingEntries ?? []);
  const usersList = useSelector((state) => state.users.users);
  const [descriptionValue, setDescriptionValue] = useState(data?.description ?? "");
  const [richDescriptionValue, setRichDescriptionValue] = useState(
    data?.richDescription ?? data?.description ?? "",
  );
  const [hazardousMaterialsValue, setHazardousMaterialsValue] = useState(
    data?.hazardousDeclaration?.hazardousMaterialsList ?? "",
  );
  const [assistanceInlineState, setAssistanceInlineState] = useState(() =>
    buildInitialAssistanceState(data, usersList)
  );
  const user = useSelector((store) => store.user);
  const projectLabel = useSelector((state) =>
    projectDisplayText(state, data?.redPandaProjects, data?.project)
  );
  const isBookingAdmin = checkIsBookingAdmin(user);
  const canEditDescription = canEditBookingDescription({
    allowDescriptionEdit,
    isAdminContext,
    isBookingAdmin,
  });
  const canEditHazardousDetails = canEditHazardousMaterials({
    canEditDescription,
    hazardousDeclaration: data?.hazardousDeclaration,
  });
  const canEditAssistance = canEditBookingAssistance({
    allowAssistanceEdit,
    isAdminContext,
    isBookingAdmin,
  });
  const incomingAssistent = data?.assistent;
  const incomingAssistentTime = data?.assistentTime;
  const incomingBookingDataId = data?.id;
  const rowStyle = { background: "var(--one-color-cobas-accent-gray-100)" };
  const { dispatchAction } = useContext(PaginationContext);
  const {
    isChangeEndTimeOpen,
    bookingForEndTimeChange,
    handleOpenChangeEndTimeModal,
    handleCloseChangeEndTimeModal,
    handleSaveEndTime,
  } = useChangeEndTimeModal({ client });

  const [columnDefs, setColumnDefs] = useState(() =>
    buildColumnDefs(displayPreset),
  );

  useEffect(() => {
    const newDefs = buildColumnDefs(displayPreset);
    setColumnDefs(newDefs); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayPreset]);

  useEffect(() => {
    setDescriptionValue(data?.description ?? "");
  }, [data?.description]);

  useEffect(() => {
    setRichDescriptionValue(data?.richDescription ?? data?.description ?? "");
  }, [data?.richDescription, data?.description]);

  useEffect(() => {
    setHazardousMaterialsValue(
      data?.hazardousDeclaration?.hazardousMaterialsList ?? "",
    );
  }, [data?.hazardousDeclaration?.hazardousMaterialsList]);

  useEffect(() => {
    const nextInlineState = buildInitialAssistanceState(
      {
        assistent: incomingAssistent,
        assistentTime: incomingAssistentTime,
      },
      usersList,
    );
    setAssistanceInlineState(nextInlineState);
  }, [incomingBookingDataId, incomingAssistent, incomingAssistentTime, usersList]);

  useEffect(() => {
    setAssistanceInlineState((prev) => syncAssistanceInlineStateName(prev, usersList));
  }, [usersList]);

  const bookingId = resolveBookingId(data);

  const handleAssistanceApprovalSync = async ({
    nextStatus,
    assistantId,
    assistantDuration,
  }) => {
    if (!bookingId || !nextStatus) return false;

    try {
      const variables = buildApproveBookingVariables({
        bookingId,
        nextStatus,
        assistantId,
        assistantDuration,
        user,
      });

      const result = await client.mutate({
        mutation: APPROVE_BOOKING,
        variables,
      });

      const payload = result?.data?.approveBooking;

      return await processApproveBookingSuccess({
        payload,
        onAssistanceUpdateComplete,
      });
    } catch (error) {
      return false;
    }
  };

  const onRichDescriptionSave = async ({ richDescription, description }) => {
    if (!bookingId) return;
    if (
      (richDescription || "") === (richDescriptionValue || "") &&
      (description || "") === (descriptionValue || "")
    ) {
      return;
    }
    setFetchingState(dispatchAction, setStatus, true);
    const result = await client.mutate({
      mutation: QUICK_EDIT_BOOKING,
      fetchPolicy: "no-cache",
      variables: {
        input: {
          bookingId,
          description,
          richDescription,
          createdByEmail: user?.email,
        },
      },
    });
    const payload = result?.data?.quickEditBooking;
    if (!payload?.success) {
      throwQuickEditFailure({
        payload,
        dispatchAction,
        setStatus,
        fallbackMessage: "Description update failed",
      });
    }
    setRichDescriptionValue(richDescription);
    setDescriptionValue(description);
    await finalizeQuickEdit({
      payload,
      dispatchAction,
      setStatus,
      onSuccess: async () => {
        if (onDescriptionUpdate) await onDescriptionUpdate(description);
      },
    });
  };

  const onHazardousMaterialsSave = (nextVal) =>
    handleHazardousMaterialsSave({
      nextVal,
      bookingId,
      hazardousDeclaration: data?.hazardousDeclaration,
      userEmail: user?.email,
      client,
      setStatus,
      dispatchAction,
      onHazardousMaterialsUpdate,
    });

  const onInlineAssistanceSaved = (args) =>
    handleAssistanceSaved({
      ...args,
      dataId: data?.id,
      usersList,
      setAssistanceInlineState,
    });

  const defaultColDef = {
    flex: 1,
    resizable: true,
    suppressMenu: true,
  };

  let attr = {
    columnDefs,
    animateRows: true,
    rowExport: false,
    defaultToolPanel: "filters",
    suppressContextMenu: true,
    hiddenByDefault: true,
    rowData: rowData,
  };

  const effectiveBooking = buildEffectiveBooking(data, assistanceInlineState);

  const gridItemStyle = { alignSelf: "start", maxWidth: '483.33px', display: 'block' };

  return (
    <div
      style={{
        padding: "0.625rem 2rem 1rem 2rem",
        backgroundColor: "#FBFBFA",
        display: "flex",
        flexDirection: "column",
        gap: "1.5rem",
      }}
    >
      <OwcGrid container columns={3} vGutter={24} hGutter={1}>
        <OwcGrid item xl={1} l={1} m={1} xs={3} s={3} style={gridItemStyle}>
          <div
            style={{
              display: "flex",
              gap: "1.5rem",
              flexDirection: "column",
              flexWrap: "nowrap",
            }}
          >
            {/* Date and time range */}
            <div >
              <DateTimeRange
                start={data?.dateFrom}
                end={data?.dateTo}
                timezone={user?.timezone}
                repetitionPattern={data?.repetitionPattern}
                handleOpenChangeEndTimeModal={handleOpenChangeEndTimeModal}
              />
            </div>

            {/* Description*/}
            <div style={{ minWidth: "fit-content" }}>
              <InlineRichDescriptionField
                label="Description"
                value={richDescriptionValue}
                allowEdit={canEditDescription}
                placeholder="Enter description"
                onSave={onRichDescriptionSave}
              />
            </div>

            {/* Project */}
            <ProjectField data={data} projectLabel={projectLabel} />
          </div>
        </OwcGrid>

        <OwcGrid item xl={1} l={1} m={1} xs={3} s={3} style={gridItemStyle}>
          <div
            style={{ display: "flex", gap: "1.5rem", flexDirection: "column" }}
          >
            {/* eLN ID or Action type */}
            <ActionTypeField type={data?.type} elnId={data?.elnId} />

            {/* Approval status */}
            <div>
              <OwcTypography variant="caption">
                {BookingMeta.fields.hazardous.text}
              </OwcTypography>
              <HazardousApprovalActions
                booking={data}
                setOpenHazardousRejectDialog={setOpenHazardousRejectDialog}
                setHazardousBookingToReject={setHazardousBookingToReject}
                setOpenAssistanceDialog={setOpenAssistanceDialog}
                setBookingForAssistance={setBookingForAssistance}
                setIsEditMode={setIsEditMode}
              />
            </div>

            {/* Reason for rejection, show only if booking is rejected */}
            <RejectionReasonField approval={data?.approval} />
          </div>
        </OwcGrid>

        <OwcGrid item xl={1} l={1} m={1} xs={3} s={3} style={gridItemStyle}>
          <div
            style={{ display: "flex", gap: "1.5rem", flexDirection: "column" }}
          >
            {/* Hazardous materials (editable) */}
            <div>
              <InlineEditableField
                label="Hazardous materials details"
                value={hazardousMaterialsValue}
                allowEdit={canEditHazardousDetails}
                placeholder="Enter hazardous materials list"
                onSave={onHazardousMaterialsSave}
                displayFormatter={(val) => valueOrEmpty(val, false, "-")}
              />
            </div>

            {/* Assistance details  */}
            <div>
              <AssistanceDetails
                assistanceDeclaration={data?.assistanceDeclaration}
                assistanceDuration={assistanceInlineState.assistantDuration}
                assistantName={assistanceInlineState.assistantName}
                approvalStatus={data?.approval?.status}
                booking={effectiveBooking}
                client={client}
                allowEdit={canEditAssistance}
                setStatus={setStatus}
                dispatchAction={dispatchAction}
                onAssistanceUpdateComplete={onAssistanceUpdateComplete}
                onAssistanceSaved={onInlineAssistanceSaved}
                onApprovalStatusSyncRequested={handleAssistanceApprovalSync}
                onClick={() =>
                  openAssistanceDialog({
                    data,
                    setBookingForAssistance,
                    setOpenAssistanceDialog,
                    setIsEditMode,
                  })
                }
              />
            </div>
          </div>
        </OwcGrid>

        <OwcGrid item xl={3} l={3} m={3} xs={3} s={3} style={gridItemStyle}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <BookingUsersField bookedUsers={data?.bookedUsers} />
            {(user?.isPredUser || user?.isGredUser) && (
              <CreatedByCreatedOn
                createdBy={data?.createdBy}
                createdByEmail={data?.createdByEmail}
                createdAt={data?.createdAt}
                usersList={usersList}
                timezone={user?.timezone}
              />
            )}
          </div>
        </OwcGrid>
      </OwcGrid>

      {/* Equipment details */}
      <div style={{ backgroundColor: "#FFFFFF" }}>
        <div
          style={{
            paddingTop: ".5rem",
          }}
        >
          <OwcTypography
            variant="button"
            style={{
              paddingTop: ".625rem",
              paddingBottom: ".625rem",
              paddingRight: ".5rem",
              paddingLeft: "1rem",
            }}
          >
            Equipment details
          </OwcTypography>
        </div>
        <div
          style={{
            paddingRight: "1rem",
            paddingLeft: "1rem",
          }}
        >
          <div className="ag-theme-alpine">
            <div className="sub-table-grid">
              <DLabGrid
                {...attr}
                pagination={false}
                suppressPaginationPanel={false}
                defaultColDef={defaultColDef}
                masterDetail={true}
                detailRowAutoHeight={true}
                detailCellRenderer={SlotDetailCellRenderer}
                autoGroupColumnDef={"autoGroupColumnDef"}
                rowStyle={rowStyle}
                domLayout={"autoHeight"}
                height={"100%"}
              />
            </div>
          </div>
        </div>
      </div>
      <ChangeBookingEndTimeModal
        isOpen={isChangeEndTimeOpen}
        onClose={handleCloseChangeEndTimeModal}
        booking={bookingForEndTimeChange || data}
        onSave={handleSaveEndTime}
        initialSelectedResourceId={(bookingForEndTimeChange || data)?.resource}
      />
    </div>
  );
};

export default compose(withApollo)(BookingDetailRenderer);
